import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon, Spinner } from '~/components';
import getGradientColor from '~/routes/app/pipeline/opportunities/components/gradient';
import { colors, weights } from '~/styles';

const Container = styled.div`
  width: 100%;
  margin-top: 1rem;
`;

const Header = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
`;

const Heading = styled.div`
  font-size: .875rem;
  font-weight: ${weights.bold};
`;

const Subheading = styled.div`
  color: ${colors.grey55};
  font-size: 0.875rem;
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  color: ${({ color }) => color};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  white-space: nowrap;
  background: ${({ background }) => background};
  border-radius: 999rem;
  margin: 0 0.5rem;
`;

const Dot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.5rem;
  background: ${({ color }) => color};
  border-radius: 50%;
`;

const Progress = styled.div`
  flex: 1;
  min-height: 4.75rem;
  margin-right: 1.5rem;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ProgressName = styled.span`
  display: flex;
  align-items: center;
`;

const ProgressValue = styled.span`
  font-size: 1rem;
  font-weight: ${weights.bold};
`;

const Meter = styled.div`
  display: flex;
  min-height: 0.75rem;
  background: ${colors.white};
  border: 1px solid ${colors.grey10};
  position: relative;
  border-radius: 0.5rem;
  padding: 0.125rem;
  min-width: 100%;
  overflow: hidden;
`;

const InsideBar = styled.div`
  min-width: 0.1rem;
  border-radius: 0.25rem;
  width: ${({ width }) => width};
  background: ${({ color }) => color};
  transition: width 0.5s linear;
`;

const Timing = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  color: ${colors.grey55};
  font-size: 0.875rem;
`;

const Schedule = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: ${colors.grey5};
  border-radius: 0.3125rem;

  ${Header} {
    margin-top: 0;
    margin-left: auto;
    padding: 0;
    text-align: right;
    white-space: nowrap;
  }
`;

const ScheduleIcon = styled(Icon)`
  margin-right: 0.75rem;
  color: ${colors.grey55};
`;

const ScheduleText = styled.div`
  font-size: 0.875rem;

  strong {
    display: block;
  }

  span {
    color: ${colors.grey55};
  }
`;

const steps = [
  { id: 'pending', name: 'Pending', percentage: 0 },
  { id: 'initializing', name: 'Initializing', percentage: 5 },
  { id: 'members', name: 'Migrating Members', percentage: 10 },
  { id: 'clients', name: 'Migrating Clients', percentage: 18 },
  { id: 'contacts', name: 'Migrating Contacts', percentage: 25 },
  { id: 'expense_categories', name: 'Migrating Expense Categories', percentage: 30 },
  { id: 'projects', name: 'Migrating Projects', percentage: 38 },
  { id: 'tasks', name: 'Migrating Task Assignments', percentage: 46 },
  { id: 'project_team', name: 'Migrating Project Team Members', percentage: 54 },
  { id: 'invoices', name: 'Migrating Invoices', percentage: 64 },
  { id: 'expenses', name: 'Migrating Expenses', percentage: 76 },
  { id: 'time_entries', name: 'Migrating Time Entries', percentage: 90 },
  { id: 'completed', name: 'Completed', percentage: 100 },
  { id: 'failed', name: 'Failed', percentage: 0 },
  { id: 'never-run', name: 'Never Been Run', percentage: 0 },
];

export function formatDuration(seconds) {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

// The estimate is deliberately rough and should err on the long side, so the model's number is the low end.
export function estimateRange(seconds) {
  return { low: seconds, high: seconds * 2 };
}

export function formatRange(low, high) {
  const lowText = formatDuration(low);
  const highText = formatDuration(high);
  if (highText === 'less than a minute' || highText === '1 minute') return 'Less than a minute';

  const pattern = /^(\d+) ((minute|hour)s?)$/;
  const lowMatch = lowText.match(pattern);
  const highMatch = highText.match(pattern);
  if (lowMatch && highMatch && lowMatch[3] === highMatch[3]) {
    return `${lowMatch[1]}–${highMatch[1]} ${highMatch[2]}`;
  }
  return `${lowText} – ${highText}`;
}

export function SyncStatus({ sync }) {
  const statusId = sync?.statusId ?? 'never-run';

  const status =
    statusId === 'never-run'
      ? { label: 'Never run', color: colors.grey55, background: colors.grey5 }
      : statusId === 'completed'
        ? { label: 'Completed', color: colors.success, background: colors.success10 }
        : statusId === 'failed'
          ? { label: 'Failed', color: colors.danger, background: colors.danger5 }
          : { label: 'In progress', color: colors.primary, background: colors.primary10 };

  return (
    <Status color={status.color} background={status.background}>
      <Dot color={status.color} />
      {status.label}
    </Status>
  );
}

export function SyncProgress({ sync }) {
  const currentStep = steps.find((step) => step.id === sync?.statusId) || steps[0];
  const isRunning = Boolean(sync?.isRunning);

  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (progress < currentStep.percentage) {
      const timer = setTimeout(() => setProgress(progress + 1), 30);
      return () => clearTimeout(timer);
    } else if (progress > currentStep.percentage) {
      setProgress(currentStep.percentage);
    }
  }, [progress, currentStep.percentage]);

  const [now, setNow] = useState(moment());
  useEffect(() => {
    if (!isRunning) return undefined;
    const timer = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(timer);
  }, [isRunning]);

  // Fast phases hold their status for a moment only, so the label follows the animated progress.
  const displayStep = _.findLast(steps, (step) => step.percentage > 0 && step.percentage <= progress) ?? currentStep;

  const startedAt = sync?.startedAt ? moment(sync.startedAt) : sync?.createdAt ? moment(sync.createdAt) : null;
  const elapsedSeconds = startedAt ? Math.max(0, now.diff(startedAt, 'seconds')) : null;
  const estimate = sync?.estimatedSeconds ? estimateRange(sync.estimatedSeconds) : null;

  return (
    <Progress>
      {isRunning && (
        <>
          <ProgressHeader>
            <ProgressName>
              <div style={{ marginRight: '0.5rem' }}>{displayStep.name}</div>
              {progress < 100 && <Spinner />}
            </ProgressName>
            <ProgressValue>{progress}%</ProgressValue>
          </ProgressHeader>
          <Meter>
            <InsideBar width={`${progress}%`} color={getGradientColor(currentStep.percentage)} />
          </Meter>

          {elapsedSeconds != null && (
            <Timing>
              <span>Running for {formatDuration(elapsedSeconds)}</span>
              {estimate && <span>Estimated total: {formatRange(estimate.low, estimate.high)}</span>}
            </Timing>
          )}
        </>
      )}
    </Progress>
  );
}

export default function SyncStatusBar({ sync }) {
  if (!sync) {
    sync = { statusId: 'never-run' };
  }

  const isCompleted = sync?.statusId === 'completed';
  const isFailed = sync?.statusId === 'failed';
  const isNeverRun = sync?.statusId === 'never-run';
  const isRunning = Boolean(sync?.isRunning);
  const isFinished = isCompleted || isFailed;

  const [now, setNow] = useState(moment());
  useEffect(() => {
    if (!isRunning) return undefined;
    const timer = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(timer);
  }, [isRunning]);

  const startedAt = sync.startedAt ? moment(sync.startedAt) : sync.createdAt ? moment(sync.createdAt) : null;
  const endedAt = sync.endedAt ? moment(sync.endedAt) : null;
  const elapsedSeconds = startedAt ? Math.max(0, (endedAt ?? now).diff(startedAt, 'seconds')) : null;
  const estimate = sync.estimatedSeconds ? estimateRange(sync.estimatedSeconds) : null;
  const records = sync.recordCount != null ? sync.recordCount.toLocaleString() : null;

  const header = (
    <Header>
      <Heading>{isNeverRun ? 'Harvest Migration' : `${sync.fullSync ? 'Full' : 'Incremental'} Migration`}</Heading>
      <Subheading>
        {isNeverRun
          ? 'No migration has run yet'
          : isCompleted
            ? sync.changeCount == null
              ? 'Migration completed'
              : sync.changeCount > 0
                ? `${sync.changeCount.toLocaleString()} ${sync.changeCount === 1 ? 'record' : 'records'} added or updated`
                : 'No changes found'
            : isFailed
              ? 'Migration failed'
              : records
                ? sync.recordCount > 0
                  ? `${records} ${sync.recordCount === 1 ? 'record' : 'records'}`
                  : 'Migration running'
                : 'Counting from Harvest'}
      </Subheading>
    </Header>
  );

  const schedule = !isNeverRun && startedAt && (
    <Schedule>
      <ScheduleIcon icon="clock" />
      <ScheduleText>
        {isFinished ? (
          <>
            <strong>
              {isFailed ? `Failed at ${(endedAt ?? now).format('l LT')}` : `Completed at ${endedAt.format('l LT')}`}
            </strong>
            <span>
              {isFailed
                ? `Started at ${startedAt.format('LT')} · Stopped after ${formatDuration(elapsedSeconds)}`
                : `Started at ${startedAt.format('LT')} · Took ${formatDuration(elapsedSeconds)}`}
            </span>
          </>
        ) : (
          <>
            <strong>Started at {startedAt.format('LT')}</strong>
            {estimate ? (
              <span>
                Completing: {startedAt.clone().add(estimate.low, 'seconds').format('LT')} –{' '}
                {startedAt.clone().add(estimate.high, 'seconds').format('LT')}
              </span>
            ) : (
              <span>Estimating the completion time</span>
            )}
          </>
        )}
      </ScheduleText>
      {header}
    </Schedule>
  );

  return <Container>{schedule}</Container>;
}
